import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {convertToModels} from 'shells/filter-capsule/convert-to-models';
import {SearchBar} from 'shells/search-bar/search-bar';

import {Button} from '../button/button';
import {CheckboxButton} from '../button/checkbox-button/checkbox-button';
import {ButtonGroup} from '../button-group/button-group';
import {FilterCapsule} from '../filter-capsule/filter-capsule';

import {
    convertLastUpdatedOptionToRelativeTime,
    convertRelativeTimeToLastUpdatedOption,
} from './helpers';

import styles from './expanded-filter-bar.module.css';

type Props = {
    ownerFilterProps?: {
        ownerFilter: string | null | undefined;
        collection: any[];
    };
    statusFilterProps?: {
        statusFilter: string | null | undefined;
        statusOptions: any[];
        statusDisabled: boolean;
    };
    lastUpdatedFilterProps?: {
        lastUpdatedFilter: string | null | undefined;
        lastUpdatedOptions: any[];
    };
    justMineFilterProps?: {
        isFilteringOnCreatorUserId: boolean;
        userId: string;
    };
    archivedFilterProps?: {
        isFilteringOnArchived: boolean;
    };
    ignoredFilterProps?: {
        isFilteringOnIgnored: boolean;
    };
    publicFilterProps?: {
        isFilteringOnPublic: boolean;
    };
    searchFilterProps?: {
        searchValue: string;
        onUpdateSearch: (newSearch: string) => void;
    };
    isFiltering: boolean;
    noun: 'broadcast' | 'newsletter' | 'drip sequence' | 'audience' | 'form' | 'template';
    count: number;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

export function ExpandedFilterBar(props: Props) {
    const {
        ownerFilterProps,
        statusFilterProps,
        lastUpdatedFilterProps,
        justMineFilterProps,
        ignoredFilterProps,
        publicFilterProps,
        searchFilterProps,
        isFiltering,
        noun,
        count,
        onUpdateFilters,
        onRemoveAllFilters,
    } = props;

    return (
        <ButtonGroup flexWrap={{justifyContent: 'start'}}>
            {ownerFilterProps && (
                <div>
                    <FilterCapsule
                        key='owner'
                        size='big'
                        collection={convertToModels(ownerFilterProps.collection)}
                        filter={ownerFilterProps.ownerFilter}
                        filterName='owner'
                        schemaField={{
                            name: 'owner',
                            type: 'relationship',
                            title: 'Owner',
                            description: `A list of Nutshell users that a ${noun} must be owned by.`,
                            relationships: ['users', 'teams'],
                            isFilterable: true,
                            isDisplayable: true,
                            isSortable: true,
                            isAndable: true,
                            size: 'md',
                        }}
                        onSubmit={(newFilter: FilterObject) => {
                            onUpdateFilters({
                                owner: newFilter.owner,
                            });
                        }}
                        onRemove={() => {
                            onUpdateFilters({
                                owner: null,
                            });
                        }}
                    />
                </div>
            )}
            {statusFilterProps && (
                <div className={styles.status}>
                    <FilterCapsule
                        hasSearch={false}
                        key='status'
                        size='big'
                        disabled={statusFilterProps.statusDisabled}
                        collection={convertToModels(statusFilterProps.statusOptions)}
                        filter={statusFilterProps.statusFilter}
                        filterName={'status'}
                        schemaField={{
                            type: 'enum',
                            cellType: 'description',
                            enum: statusFilterProps.statusOptions,
                            title: 'Status',
                            description: `Status of the ${noun}`,
                            isFilterable: true,
                            isDisplayable: true,
                            isSortable: true,
                            isAndable: false,
                            size: 'sm',
                            name: 'status',
                        }}
                        onSubmit={(newFilter: FilterObject) => {
                            onUpdateFilters({
                                status: newFilter.status,
                            });
                        }}
                        onRemove={() => {
                            onUpdateFilters({
                                status: '',
                            });
                        }}
                    />
                </div>
            )}
            {lastUpdatedFilterProps && (
                <div className={styles['last-updated']}>
                    <FilterCapsule
                        hasSearch={false}
                        key='status'
                        size='big'
                        collection={convertToModels(lastUpdatedFilterProps.lastUpdatedOptions)}
                        filter={convertRelativeTimeToLastUpdatedOption(
                            lastUpdatedFilterProps.lastUpdatedFilter
                        )}
                        filterName={'lastUpdated'}
                        schemaField={{
                            type: 'enum',
                            cellType: 'description',
                            enum: lastUpdatedFilterProps.lastUpdatedOptions,
                            title: 'Last updated',
                            description: `Last updated time`,
                            isFilterable: true,
                            isDisplayable: true,
                            isSortable: false,
                            isAndable: true,
                            size: 'sm',
                            name: 'lastUpdated',
                        }}
                        onSubmit={(newFilter: FilterObject) => {
                            const lastUpdatedRelativeTime =
                                convertLastUpdatedOptionToRelativeTime(newFilter);
                            onUpdateFilters({
                                lastUpdated: lastUpdatedRelativeTime,
                            });
                        }}
                        onRemove={() => {
                            onUpdateFilters({
                                lastUpdated: '',
                            });
                        }}
                    />
                </div>
            )}
            {justMineFilterProps && (
                <div className={styles['expanded-filter-checkbox']}>
                    <CheckboxButton
                        label='Just mine'
                        checked={justMineFilterProps.isFilteringOnCreatorUserId}
                        onChange={() => {
                            if (justMineFilterProps.isFilteringOnCreatorUserId) {
                                onUpdateFilters({creatorUserId: ''});
                            } else {
                                onUpdateFilters({creatorUserId: justMineFilterProps.userId});
                            }
                        }}
                    />
                </div>
            )}
            {publicFilterProps && (
                <div className={styles['expanded-filter-checkbox']}>
                    <CheckboxButton
                        label='On preference center'
                        checked={publicFilterProps.isFilteringOnPublic}
                        onChange={() => {
                            onUpdateFilters({
                                public: !publicFilterProps.isFilteringOnPublic,
                            });
                        }}
                    />
                </div>
            )}
            {ignoredFilterProps && (
                <div className={styles['expanded-filter-checkbox']}>
                    <CheckboxButton
                        label='Ignored'
                        checked={ignoredFilterProps.isFilteringOnIgnored}
                        onChange={() => {
                            onUpdateFilters({
                                ignored: !ignoredFilterProps.isFilteringOnIgnored,
                            });
                        }}
                    />
                </div>
            )}
            {searchFilterProps && (
                <div className={styles.search}>
                    <SearchBar
                        value={searchFilterProps.searchValue}
                        placeholder={`Search ${noun}s…`}
                        isClearable={true}
                        hasBorder={true}
                        autoFocus={false}
                        onChange={searchFilterProps.onUpdateSearch}
                        styleOptions={{height: '29px'}}
                        hiddenLabel='Search'
                    />
                    <div className={styles['count-text']}>{getCountText(count, noun)}</div>
                </div>
            )}
            <div className={styles['text-items']}>
                {!searchFilterProps && (
                    <div className={styles['count-text']}>{getCountText(count, noun)}</div>
                )}
                {isFiltering && (
                    <Button size='small' variant='text-primary' onClick={onRemoveAllFilters}>
                        Remove filters
                    </Button>
                )}
            </div>
        </ButtonGroup>
    );
}

function getCountText(count: number, noun: string) {
    if (count === 1) {
        return `${count} ${noun}`;
    }

    return `${count} ${noun}s`;
}
